

import type GeoJSON from 'geojson';
import turfBboxPolygon from '@turf/bbox-polygon';
import turfBooleanPointInPolygon from '@turf/boolean-point-in-polygon';
import turfBboxClip from '@turf/bbox-clip';
import { BBox } from 'geojson';
import { getCurrentAttributions } from './map-attribution';
import { anyType } from '../../types';
import { svgNS, svgTextAnchor } from '../../constants/lib/turfFunc';

let spriteContainerArr: {
  imageURL: string
  locationMap: {
    [name: string]: {
      width: number
      height: number
      x: number
      y: number
      pixelRatio: number
    }
  }
  image: HTMLImageElement
}[] | null = null;

const ADJUSTMENT_REQUIRED_ICON = [
  'highway-JP', 'national-JP'
]

// TODO：以下は一旦今だけ。後で修正する
const SVG_ICONS = [
  'marker_umbrella_disabled',
  'PrecipitationGauge01',
  'PrecipitationGauge02',
  'PrecipitationGauge03',
  'PrecipitationGauge04',
  'PrecipitationGauge05',
  'UnderpassInformation_disabled',
  'UnderpassInformation02',
  'EvacuationShelter01',
  'EvacuationShelter02',
  'EvacuationShelter03',
  'EvacuationShelter04',
  'EvacuationShelter05',
  'EvacurationShelter_shutdown',
  'StreamGauge_disabled',
  'StreamGauge03',
  'StreamGauge01',
  'StreamGauge03',
  'StreamGauge04',
  'StreamGauge05'
]

type UrlObject = { id: string, url: string };


const fetchSpriteFiles = async (urls: UrlObject[]) => {
  const spriteFiles = await Promise.all(
    urls.map(async ({ id, url }) => {
      const [json, png] = await Promise.all([
        fetch(`${url}.json`).then((resp) => resp.json()),
        fetch(`${url}.png`).then((resp) => resp.blob()),
      ]);
      return { id, json, png };
    })
  );
  return spriteFiles;
};


/* *******************
 * spriteを読み込む関数
 * *******************/
const loadSprite = async (name: string, urls: maplibregl.SpriteSpecification) => {
  if (!spriteContainerArr) {
    let locationMap, spritePngs;

    try {
      if(!Array.isArray(urls)) { 
        const spriteFiles = await Promise.all([
          fetch(`${urls}.json`).then((resp) => resp.json()),
          fetch(`${urls}.png`).then((resp) => resp.blob()),
        ]);
        [locationMap, spritePngs] = spriteFiles;

        const imageURL = URL.createObjectURL(spritePngs)
        const image = new Image()
        image.src = imageURL
        await new Promise((resolve) => (image.onload = resolve))
        spriteContainerArr = [{ imageURL, locationMap, image }];

      } else {
        fetchSpriteFiles(urls).then(async (spriteFiles) => {
          // 返ってきた全てのjsonとpng
          const spriteContainers = await Promise.all(
            spriteFiles.map(async (sprite) => {
              const imageURL = URL.createObjectURL(sprite.png);
              const image = new Image();
              image.src = imageURL;
              await new Promise((resolve) => (image.onload = resolve));
              return { imageURL, locationMap: sprite.json, image };
            })
          );
          spriteContainerArr = spriteContainers;
        });
      }
    } catch (error) {
      console.error(error) // エラーならばおそらく @1x だろう...
    }
  }

  const { image, locationMap } = spriteContainerArr?.find((spriteContainer) => Object.keys(spriteContainer.locationMap).includes(name)) || {};
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

  if (!image || !locationMap) { return null; }

  const targetSpriteLocation = locationMap[name];
  if (!targetSpriteLocation) {
    return null
  }
  const { width, height, x, y, pixelRatio } = targetSpriteLocation;
  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(image, x, y, width, height, 0, 0, width, height);
  const dataURL = canvas.toDataURL();
  canvas.remove();

  return { dataURL, width, height, pixelRatio };
}


/* *******************
 * mapの範囲を指定し、featureを取得する関数
 * *******************/
export const toFeatures = async (map: maplibregl.Map, bbox: BBox) => {
  const bboxPolygon = turfBboxPolygon(bbox) as GeoJSON.Feature<GeoJSON.Polygon>
  const currentStyle = map.getStyle()
  const backgroundLayer = currentStyle.layers.find(
    (layer) => layer.type === 'background'
  )

  const tmpBackgroundIdentifier = 'tmp__background2__' + Date.now()

  // backgroundLayerがある場合は、backgroundLayerの背景色を描画する
  if (backgroundLayer && 'paint' in backgroundLayer && backgroundLayer.paint) {
    const backgroundLayerIndex = currentStyle.layers.map((l) => l.id).indexOf(backgroundLayer.id);
    map.addLayer(
      {
        id: tmpBackgroundIdentifier,
        type: 'fill',
        source: {
          type: 'geojson',
          data: bboxPolygon,
        },
        paint: {
          'fill-color':
            'background-color' in backgroundLayer.paint
              ? backgroundLayer.paint['background-color']
              : '#fff',
          'fill-opacity': 
            ('fill-opacity' in backgroundLayer.paint 
              ? backgroundLayer.paint['fill-opacity']
              : 1) as maplibregl.DataDrivenPropertyValueSpecification<number>,
        },
      },
      currentStyle.layers[backgroundLayerIndex + 1].id
    );
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const mapStyle = map.getStyle();

  const features = map
    .queryRenderedFeatures([
      map.project([bbox[0], bbox[1]]),
      map.project([bbox[2], bbox[3]]),
    ])
    .filter((feature) =>
      feature.layer.id.startsWith('tmp__background2__')
        ? feature.layer.id === tmpBackgroundIdentifier
        : true
    )
    .map((feature) => {
      const props = {
        ...feature.properties,
        layer: feature.layer,
        _styleLayerId: feature.layer.id,
        _sourceLayer: feature.sourceLayer,
      }

      if (feature.geometry.type === 'Point') {
        if('paint' in feature.layer && feature.layer.paint) { 
          const layer = mapStyle.layers.find((layer) => layer.id === feature.layer.id)
          feature.layer.paint = layer ? layer.paint : feature.layer.paint;
        }
        
        if (turfBooleanPointInPolygon(feature.geometry, bboxPolygon)) {
          return {
            type: 'Feature',
            geometry: feature.geometry,
            properties: props,
          } as GeoJSON.Feature<GeoJSON.Point>;
        } else {
          return null;
        }
      } else {
        const clipped = turfBboxClip(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          feature.geometry,
          bbox
        )
        if (clipped.geometry.type === 'MultiPolygon') {
          clipped.geometry.coordinates = clipped.geometry.coordinates.filter(
            (x) => x.length > 0
          )
        }
        clipped.properties = props
        return clipped
      }
    })
    .filter((x) => !!x) as GeoJSON.Feature<
        GeoJSON.Geometry,
        { layer: maplibregl.LayerSpecification; _styleLayerId: string; _sourceLayer: string }
    >[];

  return features;
}



/* *******************
 * featureをsvgに変換する関数
 * *******************/
export const toSvg = async (
  map:maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.Geometry, { layer: anyType }>[],
  bbox: BBox
) => {
  const [left, bottom, right, top] = bbox
  const projectedLeftTop = map.project([left, top])
  const projectedRightTop = map.project([right, top])
  const projectedLeftBottom = map.project([left, bottom])
  const projectedRightBottom = map.project([right, bottom])
  const minX = Math.min(
    projectedLeftTop.x,
    projectedRightTop.x,
    projectedLeftBottom.x,
    projectedRightBottom.x
  )
  const maxX = Math.max(
    projectedLeftTop.x,
    projectedRightTop.x,
    projectedLeftBottom.x,
    projectedRightBottom.x
  )
  const minY = Math.min(
    projectedLeftTop.y,
    projectedRightTop.y,
    projectedLeftBottom.y,
    projectedRightBottom.y
  )
  const maxY = Math.max(
    projectedLeftTop.y,
    projectedRightTop.y,
    projectedLeftBottom.y,
    projectedRightBottom.y
  )
  const xDiff = maxX - minX
  const yDiff = maxY - minY
  const viewBox = `${minX} ${minY} ${xDiff} ${yDiff}`
  const width =
    Math.abs(
      projectedLeftTop.x -
        projectedRightTop.x +
        (projectedLeftBottom.x - projectedRightBottom.x)
    ) / 2
  const height =
    Math.abs(
      projectedLeftTop.y -
        projectedLeftBottom.y +
        (projectedRightTop.y - projectedRightBottom.y)
    ) / 2
  const svg = document.createElementNS(svgNS, 'svg')
  svg.setAttribute('xmlns', svgNS)
  svg.setAttributeNS(svgNS, 'viewBox', viewBox)
  svg.setAttributeNS(svgNS, 'width', width.toString())
  svg.setAttributeNS(svgNS, 'height', height.toString())

  const spriteURL = map.getStyle().sprite;

  const layerGroupBuffer: { [layerId: string]: SVGElement[] } = {}

  // 地物毎にSVG要素を生成
  for (const { geometry, properties } of features) {
    const { type } = geometry
    const { id: layerId, paint, layout, type: layerType } = properties.layer
    if (!layerGroupBuffer[layerId]) {
      layerGroupBuffer[layerId] = []
    }
    switch (type) {
      case 'Point':
      case 'MultiPoint': {
        const coordinatesList = Array.isArray(geometry.coordinates[0])
          ? geometry.coordinates.flat()
          : [geometry.coordinates]

        for (const coordinates of coordinatesList) {
          const { x, y } = map.project(coordinates as [number, number])

          /* ************ 
           * symbol 
           * ************/
          if (layerType === 'symbol') {
            const iconImage = layout['icon-image'];
            const textField = layout['text-field'];
            let textOffset = [0, 1];
            const textHaloWidth = paint && 'text-halo-width' in paint ? paint['text-halo-width'] : 0;
            const textSize = layout['text-size']
            let textAnchor = layout['text-anchor'] as string;
            if(iconImage && iconImage.name && ADJUSTMENT_REQUIRED_ICON.find((name) => iconImage.name.includes(name))) {
              textAnchor = 'top';
              textOffset = [0, -0.5];
            }
            
            const textFont = Array.isArray(layout['text-font'])
              ? layout['text-font'][0] : layout['text-font'];
            const textGroup: SVGGElement = document.createElementNS(svgNS, 'g');

            if (iconImage && spriteURL) {
              let sprite = await loadSprite(iconImage, spriteURL);

              if (!sprite) {
                // spriteがnullの場合、publicディレクトリのspritesディレクトリから読み込む
                const response = await fetch(`./sprites/${iconImage.name}.svg`);
                if (response.ok) {
                  const svgText = await response.text();
                  const parser = new DOMParser();
                  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
                  const svgElement = svgDoc.documentElement;
                  // 必要な属性を取得
                  const width = parseFloat(svgElement.getAttribute('width') || '0');
                  const height = parseFloat(svgElement.getAttribute('height') || '0');
                  const utf8Bytes = new TextEncoder().encode(svgText);
                  const dataURL = 'data:image/svg+xml;base64,' + btoa(String.fromCharCode(...utf8Bytes));
                  const pixelRatio = 1; // SVGの場合、pixelRatioは1と仮定
            
                  sprite = { dataURL, width, height, pixelRatio };
                } else {
                  console.error('Failed to load sprite from public directory');
                }
              }
              if (sprite) {
                const { dataURL, width, height, pixelRatio } = sprite;
                const image = document.createElementNS(svgNS, 'image');
                const xAttributeNS = SVG_ICONS.includes(iconImage.name) ? (x - (width / pixelRatio / 4)).toString() : (x - width / pixelRatio / 2).toString();
                const yAttributeNS = SVG_ICONS.includes(iconImage.name) ? (y - (height / pixelRatio / 2)).toString() : (y - height / pixelRatio).toString();
                const widthAttributeNS = SVG_ICONS.includes(iconImage.name) ? (width / pixelRatio / 2 * 0.5).toString() : (width / pixelRatio * 0.5).toString();
                const heightAttributeNS = SVG_ICONS.includes(iconImage.name) ? (height / pixelRatio / 2 * 0.5).toString() : (height / pixelRatio * 0.5).toString();

                image.setAttributeNS(svgNS, 'href', dataURL);
                image.setAttributeNS(svgNS, 'x', xAttributeNS);
                image.setAttributeNS(svgNS, 'y', yAttributeNS);
                image.setAttributeNS(svgNS, 'width',  widthAttributeNS);
                image.setAttributeNS(svgNS, 'height', heightAttributeNS);
                textGroup.append(image);
              }
            }

            // NOTE: maplibregl では layer.textField はレンダリングされてテキストが代入されているが、どうやら maplibregl では {name} のようにプレスホルダーのままのようなので、苦肉の策だが自分でレンダリングする
            let textContent = '';

            if (typeof textField === 'string') {
              const key = textField.match(/\{(.+)\}/)?.[1];
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              textContent = textField.replace(`{${key}}`, properties[key]);
            } else if (textField && textField.sections) {
              textContent = textField.sections[0].text;
            }

            if (textField && textHaloWidth > 0) {
              const textHalloColor = paint['text-halo-color'];
              const textHallo = document.createElementNS(svgNS, 'text');
              textHallo.setAttributeNS(svgNS, 'aria-hidden', 'true');
              textHallo.setAttributeNS(svgNS, 'x', (x + textOffset[0] * textSize).toString());
              textHallo.setAttributeNS(svgNS, 'y', (y + textOffset[1] * textSize).toString());
              textHallo.setAttributeNS(svgNS, 'fill', textHalloColor.toString());
              textHallo.setAttributeNS(svgNS, 'font-size', textSize.toString());
              textHallo.setAttributeNS(svgNS, 'stroke', textHalloColor.toString());
              textHallo.setAttributeNS(svgNS, 'stroke-width', (textHaloWidth * 2).toString());
              textHallo.setAttributeNS(svgNS, 'text-anchor', svgTextAnchor[textAnchor]);          // offset との組み合わせでうまく動いているのか？
              if(textFont) { textHallo.setAttributeNS(svgNS, 'font-family', textFont); }
              textHallo.textContent = textContent || textField.toString();
              textGroup.append(textHallo);
            }

            if (textField) {
              const fill = paint['text-color'];
              const text = document.createElementNS(svgNS, 'text');
              text.setAttributeNS(svgNS, 'x', (x + textOffset[0] * textSize).toString());
              text.setAttributeNS(svgNS, 'y', (y + textOffset[1] * textSize).toString());
              text.setAttributeNS(svgNS, 'fill', fill.toString());
              text.setAttributeNS(svgNS, 'font-size', textSize.toString());
              text.setAttributeNS(svgNS, 'text-anchor', svgTextAnchor[textAnchor]);
              if (textFont) {
                text.setAttributeNS(svgNS, 'font-family', textFont);
              }
              text.textContent = textContent || textField.toString();
            
              textGroup.append(text);
            }

            layerGroupBuffer[layerId].push(textGroup);
          
          /* ************
           * circle 
           * ************/
          } else if (layerType === 'circle') {
            const circle = document.createElementNS(svgNS, 'circle');
            const circleRadius = paint['circle-radius'];
            const circleColor = paint['circle-color'];
            const circleOpacity = paint['circle-opacity'];

            circle.setAttributeNS(svgNS, 'cx', x.toString());
            circle.setAttributeNS(svgNS, 'cy', y.toString());
            if(circleRadius) { circle.setAttributeNS(svgNS, 'r', circleRadius.toString()); }
            circle.setAttributeNS(svgNS, 'fill', circleColor.toString());
            if(circleOpacity) { circle.setAttributeNS(svgNS, 'opacity', circleOpacity.toString()); }
            layerGroupBuffer[layerId].push(circle);
          }
        }

        break;
      }

      case 'LineString':
      case 'MultiLineString': {
        const lineColor = paint['line-color']
        if (lineColor) {
          if (geometry.coordinates.length === 0) { break; }
          const coordinatesList = Array.isArray(geometry.coordinates[0][0])
            ? geometry.coordinates : [geometry.coordinates];

          for (const coordinates of coordinatesList) {
            const d = coordinates.map((position, index) => {
                const command = index === 0 ? 'M' : 'L';
                const { x, y } = map.project(position as [number, number]);
                return `${command} ${x},${y}`;
              }).join(' ');
            const lineWidth = paint['line-width'];
            const lineOpacity = paint['line-opacity'] || 1;
            const lineDasharray = paint['line-dasharray'];
            const lineCap = layout['line-cap'];
            const lineJoin = layout['line-join'];

            const path = document.createElementNS(svgNS, 'path')
            path.setAttributeNS(svgNS, 'class', type);
            path.setAttributeNS(svgNS, 'd', d);
            path.setAttributeNS(svgNS, 'stroke', lineColor);
            path.setAttributeNS(svgNS, 'stroke-width', lineWidth);
            path.setAttributeNS(svgNS, 'stroke-opacity', lineOpacity.toString());
            path.setAttributeNS(svgNS, 'stroke-dasharray', lineDasharray && [...lineDasharray.from].join(' '));
            path.setAttributeNS(svgNS, 'fill', 'none');
            path.setAttributeNS(svgNS, 'stroke-linecap', lineCap);
            path.setAttributeNS(svgNS, 'stroke-linejoin', lineJoin);
            layerGroupBuffer[layerId].push(path);
          }
        }

        break;
      }
      case 'Polygon': {
        const fill = paint['fill-color'];
        const stroke = paint['fill-outline-color'];
        const fillOpacity = paint['fill-opacity'] || 1;
        const lineColor = paint['line-color'];
        const lineWidth = paint['line-width'];
        const lineOpacity = paint['line-opacity'];
        if (fill || stroke) {
          if (geometry.coordinates.length === 0 || geometry.coordinates[0].length === 0) { break; }
          const points = geometry.coordinates[0].map((position) => {
              const { x, y } = map.project(position as [number, number]);
              return `${x},${y}`;
            }).join(' ');
          const polygon = document.createElementNS(svgNS, 'polygon');
          polygon.setAttributeNS(svgNS, 'class', [type, layerId].join(' '));
          polygon.setAttributeNS(svgNS, 'points', points);
          if(fill){ polygon.setAttributeNS(svgNS, 'fill', fill.toString()) };
          if(stroke) { polygon.setAttributeNS(svgNS, 'stroke', stroke); }
          if(fillOpacity) { polygon.setAttributeNS(svgNS, 'fill-opacity', fillOpacity); }
          if(lineColor) { polygon.setAttributeNS(svgNS, 'line-color', lineColor); }
          if(lineWidth) { polygon.setAttributeNS(svgNS, 'line-width', lineWidth); }
          if(lineOpacity) { polygon.setAttributeNS(svgNS, 'line-opacity', lineOpacity); }
          layerGroupBuffer[layerId].push(polygon);
        }

        break;
      }

      case 'MultiPolygon': {
        const fill = paint['fill-color'];
        const stroke = paint['fill-outline-color'];
        const fillOpacity = paint['fill-opacity'] || 1;
        const lineColor = paint['line-color'];
        const lineWidth = paint['line-width'];
        const lineOpacity = paint['line-opacity'];

        if (fill || stroke) {
          if (geometry.coordinates.length === 0 || geometry.coordinates[0].length === 0) { break; }

          const commands: anyType[] = [];
          for (const coordinatesList of geometry.coordinates) {
            for (const coordinates of coordinatesList) {
              for (let index = 0; index < coordinates.length; index++) {
                const position = coordinates[index];
                if (index === 0) {
                  commands.push('M');
                } else {
                  commands.push('L');
                }
                const { x, y } = map.project(position as [number, number]);
                commands.push([x, y]);
              }
            }
            commands.push('Z');
            const path = document.createElementNS(svgNS, 'path');
            path.setAttributeNS(svgNS, 'class', [type, layerId].join(' '));
            path.setAttributeNS(svgNS, 'd', commands.join(' '));
            if(fill) { path.setAttributeNS(svgNS, 'fill', fill.toString()); }
            if(stroke) { path.setAttributeNS(svgNS, 'stroke', stroke); }
            if(fillOpacity) { path.setAttributeNS(svgNS, 'fill-opacity', fillOpacity); }
            if(lineColor) { path.setAttributeNS(svgNS, 'line-color', lineColor); }
            if(lineWidth) { path.setAttributeNS(svgNS, 'line-width', lineWidth); }
            if(lineOpacity) { path.setAttributeNS(svgNS, 'line-opacity', lineOpacity); }
            layerGroupBuffer[layerId].push(path);
          }
        }
        break;
      }

      default: {
        console.warn(`Unhandled feature type: ${type}`);
        break;
      }
    }
  }

  const layerIdSeq = map.getStyle().layers.map((layer) => layer.id);
  const backgroundLayer = map.getStyle().layers.find((layer) => layer.type === 'background');
  for (const layerId of layerIdSeq) {
    if (layerGroupBuffer[layerId]) {
      layerGroupBuffer[layerId].reverse();
      const g = document.createElementNS(svgNS, 'g');
      for (const element of layerGroupBuffer[layerId]) {
        const svgId = backgroundLayer && layerId.startsWith('tmp__background2__') ? backgroundLayer.id : layerId;
        g.setAttributeNS(svgNS, 'id', svgId);
        g.append(element);
      }
      svg.append(g);
    }
  }

  const attributions = getCurrentAttributions();

  if (attributions.length > 0) {
    const text = document.createElementNS(svgNS, 'text');
    text.setAttributeNS(svgNS, 'x', (projectedRightBottom.x - 6).toString());
    text.setAttributeNS(svgNS, 'y', (projectedRightBottom.y - 6).toString());
    text.setAttributeNS(svgNS, 'fill', 'black');
    text.setAttributeNS(svgNS, 'font-size', '12');
    text.setAttributeNS(svgNS, 'text-anchor', 'end');
    text.setAttributeNS(svgNS, 'id', 'attribution');

    let tspan: SVGTSpanElement | null = null;
    for (const attribution of attributions) {
      const anchor = document.createElementNS(svgNS, 'a');
      anchor.setAttributeNS(svgNS, 'href', attribution.links[0].href);
      anchor.textContent = attribution.links[0].text;
      text.append(anchor);
      tspan = document.createElementNS(svgNS, 'tspan');
      tspan.textContent = ' | ';
      text.append(tspan);
    }
    if(tspan) { tspan.remove(); } // 最後の | を削除
    svg.append(text);
  }

  return { xml: `<?xml version="1.0"?>\n${svg.outerHTML}`, width, height };
}




/* *******************
 * データサイズをバイト表記で返す関数
 * *******************/
export const toByteLabel = (byte: number) => {
  let num = byte;
  let unit;
  if (num > 1024 ** 2) {
    num = Math.round((num / 1024 ** 2) * 100) / 100;
    unit = 'MB';
  } else if (num > 1024 ** 1) {
    num = Math.round((num / 1024 ** 1) * 100) / 100;
    unit = 'kB';
  } else {
    num = Math.round((num / 1024 ** 0) * 100) / 100;
    unit = 'B';
  }

  return `${num} ${unit}`;
}
